/**
 * RSM — Retail SDB Management dashboard.
 *
 * Tabs: Box Lookup / Vacant / Past Due / Access Log (per modules digest §RSM).
 */
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { GlobalCustomerSearch, KpiTile, Pill } from '@/components/primitives';
import type { CustomerSearchRow } from '@/components/primitives';
import { useConfig } from '@/contexts/ConfigContext';
import { BoxLookup } from './BoxLookup';
import { VacantList } from './VacantList';
import { PastDueList } from './PastDueList';
import { AccessLog } from './AccessLog';
import type { RsmTab, SdbBoxRow } from './types';
import './rsm.css';

/**
 * Two search entry points per gap G-RSM-01:
 *  - Box Lookup: branch + box-number inventory (current `safedepBoxSrch` path).
 *  - Customer: shared `GlobalCustomerSearch` filtered to AcctType=50 so the FSR
 *    can find a box by owner name/SSN/CIF instead of branch scoping.
 */
const TABS: ReadonlyArray<{ id: RsmTab; label: string }> = [
  { id: 'lookup', label: 'Box Lookup' },
  { id: 'customer', label: 'Customer' },
  { id: 'vacant', label: 'Vacant' },
  { id: 'pastdue', label: 'Past Due' },
  { id: 'access', label: 'Access Log' },
];

export default function RSM(): JSX.Element {
  const { config, state } = useConfig();
  const navigate = useNavigate();
  const [tab, setTab] = useState<RsmTab>('lookup');

  const onSelectBox = useCallback(
    (box: SdbBoxRow) => {
      navigate(`box/${box.acctId}`);
    },
    [navigate],
  );

  return (
    <div className="rsm-page">
      <header className="rsm-page__header">
        <div className="rsm-page__title-row">
          <h1>RSM — Safe Deposit Management</h1>
          <Pill variant="muted">Module RSM</Pill>
          {state === 'initializing' && <Pill variant="warn">System initializing</Pill>}
        </div>
        <p className="module-landing__scope">
          Box inventory, voluntary + drilled close-out, past-due escalation, access log.
          Deputies = joint owners on the box record. Waiting list removed.
        </p>
      </header>

      <section className="rsm-kpi-grid">
        <KpiTile label="Branches active" value={config.branches.length || 3} />
        <KpiTile label="Timeline rows loaded" value={config.sdbTimeline.length} />
        <KpiTile label="Approval rules" value={config.approvalRules.length} />
        <KpiTile label="SDB proration policy" value={
          (config.systemSettings?.['SdbProrationPolicy'] as string | undefined) ?? 'NoRefund'
        } />
      </section>

      <nav className="rsm-tabs" role="tablist" aria-label="RSM surfaces">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`rsm-tab ${tab === t.id ? 'is-active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="rsm-tab-panel" role="tabpanel">
        {tab === 'lookup' && <BoxLookup onSelectBox={onSelectBox} />}
        {tab === 'customer' && (
          <GlobalCustomerSearch
            filterAcctType="50"
            acctTypeLabel="SDB"
            onSelect={(row: CustomerSearchRow) => navigate(`/rcm/${row.cifId}`)}
            placeholder="SSN, name, or CIF of the box owner"
          />
        )}
        {tab === 'vacant' && <VacantList />}
        {tab === 'pastdue' && <PastDueList />}
        {tab === 'access' && <AccessLog />}
      </div>
    </div>
  );
}
